import { useEffect, useRef, useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import { MiembrosTable, Miembro } from './MiembrosTable'
import { ImportJugadores } from './ImportJugadores'
import { EntrenadoresAsignadosEquipo } from './EntrenadoresAsignadosEquipo'
import { EditarEquipoForm, EquipoEditable } from './EditarEquipoForm'
import { AnadirJugadorForm } from './AnadirJugadorForm'
import editStyles from '../adminEdit.module.css'
import { Icon } from '../../styles/Icon'

export function EquipoDetail({
  equipo,
  onClose,
  onUpdated,
}: {
  equipo: EquipoEditable
  onClose: () => void
  onUpdated: () => void
}) {
  const lang = useLang()
  const api = useAdminApi()
  const [miembros, setMiembros] = useState<Miembro[]>([])
  const panelRef = useRef<HTMLElement>(null)

  async function refresh() {
    setMiembros(await api.get(`/admin/equipos/${equipo.id}/miembros`))
  }

  // Issue #6 del vídeo: con equipos de 80+ jugadores, borrar uno a uno no es viable cuando lo que
  // se quiere es volver a importar la plantilla desde cero. Confirmación reforzada (escribir el
  // número de jugadores a borrar), igual criterio que el borrado forzado de equipo.
  async function handleVaciar() {
    if (miembros.length === 0) return
    if (!window.confirm(t('vaciarPlantillaConfirm', lang))) return
    const escrito = window.prompt(t('vaciarPlantillaEscribirNumero', lang).replace('{n}', String(miembros.length)))
    if (escrito !== String(miembros.length)) {
      if (escrito !== null) window.alert(t('vaciarPlantillaNoCoincide', lang))
      return
    }
    await api.del(`/admin/equipos/${equipo.id}/miembros`)
    await refresh()
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipo.id])

  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [equipo.id])

  return (
    <section ref={panelRef} className={editStyles.panel} aria-label={`${t('editando', lang)}: ${equipo.nombre}`}>
      <div className={editStyles.panelHeader}>
        <h3 className={editStyles.panelTitle}>
          {t('editando', lang)}: {equipo.nombre}
        </h3>
        <button onClick={onClose}><Icon name="cancelar" />{t('cancelar', lang)}</button>
      </div>
      <EditarEquipoForm equipo={equipo} onSaved={onUpdated} />
      <MiembrosTable equipoId={equipo.id} miembros={miembros} onChange={refresh} />
      <AnadirJugadorForm equipoId={equipo.id} onAdded={refresh} />
      <ImportJugadores equipoId={equipo.id} onImported={refresh} />
      <EntrenadoresAsignadosEquipo equipoId={equipo.id} />
      {/* fix-mobile-responsive: estaba justo debajo de "Guardar" del formulario, fácil de pulsar por
          error en móvil — ahora en una zona de peligro aparte, al final del panel. */}
      {miembros.length > 0 && (
        <div className={editStyles.dangerZone}>
          <h4>{t('zonaPeligro', lang)}</h4>
          <button className={editStyles.dangerButton} onClick={handleVaciar}>
            <Icon name="eliminar" />
            {t('vaciarPlantilla', lang)}
          </button>
        </div>
      )}
    </section>
  )
}
